import type { AxiosError } from 'axios';
import { get, head } from 'lodash';
import type { FormikHelpers } from 'formik/dist/types';
import { logger } from 'src/error';

export const otpErrors = {
  sms_not_sent: 'СМС с кодом подтверждения не было отправлено.',
  otp_attempts_limit: 'Превышено допустимое число попыток. Запросите новый код',
  otp_expired: 'Срок действия кода подтверждения истёк',
  otp_phone_taken: 'Пользователь с таким телефоном уже существует',
  otp_wrong_code: (attemptsLeft?: number) =>
    attemptsLeft ? `Неверный код. Осталось попыток: ${attemptsLeft}` : '',
  wrong_credentials: 'Неверный пароль',
  can_be_delivered_at: (time_left_to_deliver?: number) =>
    `Выслать код подтверждения можно через ${time_left_to_deliver} секунд`,
};

export const emailAuthErrors = {
  wrong_credentials: 'Неверный логин или пароль',
  otp_attempts_limit: 'Превышено допустимое число попыток. Запросите новый код',
  client_unconfirmed_email: 'Для смены пароля подтвердите свою почту',
  email_taken: 'Указанный адрес электронной почты уже занят',
} as const;

export const mapOtpErrorCodeToMessage = (
  errorCode: keyof typeof otpErrors,
  attemptsOrSecondsLeft?: number,
) => {
  logger(errorCode, 'Auth: otpSmsAuthErrors');

  if (errorCode === 'otp_attempts_limit') {
    return otpErrors.otp_attempts_limit;
  }
  if (errorCode === 'otp_expired') {
    return otpErrors.otp_expired;
  }
  if (errorCode === 'otp_wrong_code') {
    return otpErrors.otp_wrong_code(attemptsOrSecondsLeft);
  }
  if (errorCode === 'sms_not_sent') {
    return otpErrors.sms_not_sent;
  }
  if (errorCode === 'wrong_credentials') {
    return otpErrors.wrong_credentials;
  }
  if (errorCode === 'otp_phone_taken') {
    return otpErrors.otp_phone_taken;
  }
  if (errorCode === 'can_be_delivered_at') {
    return otpErrors.can_be_delivered_at(attemptsOrSecondsLeft);
  }

  return 'Неизвестная ошибка';
};

export const mapEmailAuthErrorCodeToMessage = (error: AxiosError) => {
  const errorCode: any = head(get(error, ['response', 'data', 'errors']));
  const message = get(emailAuthErrors, [errorCode]);

  logger(error as any, 'Auth: EmailAuthErrorCodeToMessage');
  return message ?? '';
};

/**
 * @params errorsDictionary
 * @params errorsDictionary[error_code]
 * @params errorsDictionary[error_code].name - field name
 * @params errorsDictionary[error_code].message - error translation
 *
 * @example
 * const errors = {
 *   otp_phone_taken: {
 *     name: 'phone,
 *     message: 'Пользователь с таким телефоном уже существует',
 *   },
 * };
 * mapSubmitErrorsToFields(confirmPhoneFormErrorMap)(formikHelpers.setFieldError)(error)
 * */
export const mapSubmitErrorsToFields =
  (errorsDictionary: Record<string, { name: string; message: string }>) =>
  (setFieldError: FormikHelpers<any>['setFieldError']) =>
  (errorCodesArray: string[]) => {
    logger(errorsDictionary, 'Auth: SubmitErrorsToFields');

    errorCodesArray?.forEach((code) => {
      const config = get(errorsDictionary, [code]);
      if (config) {
        setFieldError(config.name, config.message);
      }
    });
  };
